import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';

/**
 * AdminCualencuestaPanel - panel admin para cotizar / rechazar / activar / cerrar encuestas.
 */
const AdminCualencuestaPanel = () => {
  const [filter, setFilter] = useState('pending_review');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [opened, setOpened] = useState(null); // request abierta para cotizar/rechazar
  const [action, setAction] = useState(null); // 'quote' | 'reject'

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API}/cualencuesta/admin/requests`, { params: filter ? { status: filter } : {} });
      setItems(r.data.items || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error cargando solicitudes');
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const activate = async (id) => {
    if (!window.confirm('Activar esta encuesta? Confirma que el solicitante ya pago via CUALpay.')) return;
    try {
      await axios.post(`${API}/cualencuesta/admin/${id}/activate`);
      setMsg('Encuesta activada'); fetchList();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error activando');
    }
  };
  const close = async (id) => {
    if (!window.confirm('Cerrar manualmente esta encuesta?')) return;
    try {
      await axios.post(`${API}/cualencuesta/admin/${id}/close`);
      setMsg('Encuesta cerrada'); fetchList();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error cerrando');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-cualencuesta-panel">
      <header className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-lg font-bold text-gray-900">CUALencuesta - Panel admin</h3>
        <p className="text-sm text-gray-600 mt-1">Cotiza, aprueba o rechaza solicitudes de encuestas curadas.</p>
      </header>

      <div className="flex gap-2 flex-wrap" data-testid="admin-encuesta-filters">
        {['pending_review', 'quoted', 'awaiting_payment', 'active', 'closed', 'rejected', ''].map(s => (
          <button key={s || 'all'} onClick={() => setFilter(s)}
            data-testid={`admin-filter-${s || 'all'}`}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              filter === s ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300'
            }`}>
            {s ? s.replace('_', ' ') : 'todas'}
          </button>
        ))}
      </div>

      {error && <div className="rounded bg-red-50 border border-red-200 text-red-700 text-sm p-3">{error}</div>}
      {msg && <div className="rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3">{msg}</div>}

      {loading ? <p className="text-sm text-gray-500">Cargando...</p> : (
        <div className="space-y-3">
          {items.length === 0 && <p className="text-sm text-gray-500" data-testid="admin-encuesta-empty">Sin resultados.</p>}
          {items.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`admin-encuesta-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="font-semibold text-gray-900">{s.title}</h4>
                  <p className="text-xs text-gray-500">{s.requester_email} - {new Date(s.created_at).toLocaleString()}</p>
                  <p className="text-xs text-gray-600 mt-1">Categoria sugerida: <strong>{s.requested_category || 'N/A'}</strong> - Audiencia: {s.target_responses} respuestas</p>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium uppercase">{s.status}</span>
              </div>
              <p className="text-xs text-gray-700 mt-2 line-clamp-2"><strong>Proposito:</strong> {s.purpose}</p>
              {s.quote && (
                <div className="mt-2 text-xs text-gray-700 bg-purple-50 border border-purple-100 rounded p-2">
                  <strong>{s.quote.category}</strong> - ${s.quote.final_price_usd} - Premio total {s.quote.premium_total_cc} Cc - Modificadores: {(s.quote.modifiers_applied || []).join(', ') || 'ninguno'}
                </div>
              )}
              <div className="mt-3 flex gap-2 flex-wrap">
                {(s.status === 'pending_review' || s.status === 'quoted') && (
                  <>
                    <button data-testid={`admin-quote-${s.id}`} onClick={() => { setOpened(s); setAction('quote'); }}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700">Cotizar</button>
                    <button data-testid={`admin-reject-${s.id}`} onClick={() => { setOpened(s); setAction('reject'); }}
                      className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded hover:bg-red-700">Rechazar</button>
                  </>
                )}
                {(s.status === 'quoted' || s.status === 'awaiting_payment') && (
                  <button data-testid={`admin-activate-${s.id}`} onClick={() => activate(s.id)}
                    className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded hover:bg-emerald-700">Activar (pago confirmado)</button>
                )}
                {s.status === 'active' && (
                  <button data-testid={`admin-close-${s.id}`} onClick={() => close(s.id)}
                    className="px-3 py-1.5 bg-gray-700 text-white text-xs font-semibold rounded hover:bg-gray-800">Cerrar</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {opened && action === 'quote' && (
        <QuoteModal req={opened} onClose={() => { setOpened(null); setAction(null); }}
          onDone={(m) => { setOpened(null); setAction(null); setMsg(m); fetchList(); }}
          onError={setError} />
      )}
      {opened && action === 'reject' && (
        <RejectModal req={opened} onClose={() => { setOpened(null); setAction(null); }}
          onDone={(m) => { setOpened(null); setAction(null); setMsg(m); fetchList(); }}
          onError={setError} />
      )}
    </div>
  );
};

const MOD_LIST = [
  { id: 'sponsored', label: 'Patrocinada (+50%)' },
  { id: 'executive_report', label: 'Informe ejecutivo (+30%)' },
  { id: 'urgent_48h', label: 'Urgente 48h (+25%)' },
  { id: 'non_profit', label: 'Sin animo de lucro (-40%)' },
  { id: 'internal_governance', label: 'Gobernanza interna (gratis)' },
  { id: 'loyalty_repeat', label: 'Cliente recurrente (-15%)' },
];

const QuoteModal = ({ req, onClose, onDone, onError }) => {
  const [category, setCategory] = useState(req.requested_category || 'L2');
  const [modifiers, setModifiers] = useState([]);
  const [customPrice, setCustomPrice] = useState('');
  const [premiumTotal, setPremiumTotal] = useState('');
  const [note, setNote] = useState('');
  const [duration, setDuration] = useState(14);
  const [submitting, setSubmitting] = useState(false);

  const toggleMod = (id) => setModifiers(m => m.includes(id) ? m.filter(x => x !== id) : [...m, id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        category, modifiers, duration_days: parseInt(duration, 10),
        custom_price_usd: customPrice !== '' ? parseFloat(customPrice) : null,
        premium_total_cc: premiumTotal !== '' ? parseInt(premiumTotal, 10) : null,
        note: note || null,
      };
      await axios.post(`${API}/cualencuesta/admin/requests/${req.id}/quote`, payload);
      onDone('Cotizacion guardada');
    } catch (e) {
      onError(e.response?.data?.detail || 'Error cotizando');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-quote-modal">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b">
          <h3 className="text-lg font-bold">Cotizar: {req.title}</h3>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Categoria</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="admin-quote-category"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {['L1', 'L2', 'L3', 'L4', 'L5'].map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <p className="block text-xs font-medium text-gray-700 mb-1">Modificadores</p>
            <div className="space-y-1">
              {MOD_LIST.map(m => (
                <label key={m.id} className="flex items-center gap-2 text-xs text-gray-700">
                  <input type="checkbox" checked={modifiers.includes(m.id)} onChange={() => toggleMod(m.id)}
                    data-testid={`admin-quote-mod-${m.id}`} />
                  {m.label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Precio override (USD)</label>
              <input type="number" step="0.01" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)}
                data-testid="admin-quote-custom-price" placeholder="auto"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Premio total Cc</label>
              <input type="number" value={premiumTotal} onChange={(e) => setPremiumTotal(e.target.value)}
                data-testid="admin-quote-premium" placeholder="auto"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Duracion (dias)</label>
            <input type="number" min={1} max={90} value={duration} onChange={(e) => setDuration(e.target.value)}
              data-testid="admin-quote-duration"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nota interna</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              data-testid="admin-quote-note"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancelar</button>
          <button data-testid="admin-quote-submit" onClick={submit} disabled={submitting}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {submitting ? 'Enviando...' : 'Enviar cotizacion'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RejectModal = ({ req, onClose, onDone, onError }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { onError('Indica un motivo'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/cualencuesta/admin/requests/${req.id}/reject`, { reason });
      onDone('Solicitud rechazada');
    } catch (e) {
      onError(e.response?.data?.detail || 'Error rechazando');
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-reject-modal">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="p-5 border-b"><h3 className="text-lg font-bold">Rechazar: {req.title}</h3></div>
        <div className="p-5">
          <label className="block text-xs font-medium text-gray-700 mb-1">Motivo</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
            data-testid="admin-reject-reason"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancelar</button>
          <button data-testid="admin-reject-submit" onClick={submit} disabled={submitting}
            className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50">
            {submitting ? 'Enviando...' : 'Rechazar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminCualencuestaPanel;
